/**
 * Tier utility functions for checking user subscription status and limits
 */

// Tier constants
export const TIERS = {
  FREE: 'free',
  PRO: 'pro'
};

// Tier limits
export const TIER_LIMITS = {
  [TIERS.FREE]: {
    maxQuotecards: 5,
    audiogramEnabled: true,
    promotionKitEnabled: false,
    unlimitedQuotecards: false
  },
  [TIERS.PRO]: {
    maxQuotecards: Infinity,
    audiogramEnabled: true,
    promotionKitEnabled: true,
    unlimitedQuotecards: true
  }
};

/**
 * Check if subscription is expired
 * @param {Object} user - User object with subscription_expires_at property
 * @returns {boolean} - True if subscription is expired
 */
export const isSubscriptionExpired = (user) => {
  if (!user || user.tier === TIERS.FREE) {
    return false; // Free tier doesn't expire
  }
  
  if (!user.subscription_expires_at) {
    return true; // No expiration date means expired/invalid
  }
  
  return new Date(user.subscription_expires_at) < new Date();
};

/**
 * Get effective tier (checks if subscription expired)
 * @param {Object} user - User object with tier and subscription_expires_at properties
 * @returns {string} - Effective tier ('free' or 'pro')
 */
export const getEffectiveTier = (user) => {
  const forcePro = (
    (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENABLE_PRO_TESTING === 'true') ||
    (typeof window !== 'undefined' && (
      (new URLSearchParams(window.location.search)).get('pro') === '1' ||
      (new URLSearchParams(window.location.search)).get('forcePro') === '1' ||
      (window.localStorage && window.localStorage.getItem('force_pro') === 'true')
    ))
  );

  if (forcePro) {
    try { console.log('[Tier] Pro testing override active'); } catch (e) {}
    return TIERS.PRO;
  }

  if (!user) return TIERS.FREE;
  
  if (user.tier === TIERS.PRO && !isSubscriptionExpired(user)) {
    return TIERS.PRO;
  }
  
  return TIERS.FREE;
};

/**
 * Check if user has exceeded download limit for Free tier
 * @param {Object} user - User object with tier property
 * @param {number} currentDownloads - Current number of downloads
 * @returns {boolean} - True if limit exceeded
 */
export const checkDownloadLimit = (user, currentDownloads = 0) => {
  const effectiveTier = getEffectiveTier(user);
  
  if (!user || effectiveTier === TIERS.PRO) {
    return false; // Pro users have unlimited downloads
  }
  
  return currentDownloads >= TIER_LIMITS[TIERS.FREE].maxQuotecards;
};

/**
 * Check if user can download quote card
 * @param {Object} user - User object with tier and usage_count properties
 * @returns {boolean} - True if user can download quote card
 */
export const canDownloadQuotecard = (user) => {
  if (!user) return false;
  const effectiveTier = getEffectiveTier(user);
  
  if (effectiveTier === TIERS.PRO) {
    return true; // Pro users have unlimited downloads
  }
  
  // Free users: check usage count
  return (user.usage_count || 0) < TIER_LIMITS[TIERS.FREE].maxQuotecards;
};

/**
 * Check if user can download audiogram
 * @param {Object} user - User object with tier property
 * @returns {boolean} - True if user can download audiogram
 */
export const canDownloadAudiogram = (user) => {
  if (!user) return false;
  return true; // Both Free and Pro can download audiograms
};

/**
 * Check if user can send promotion kit
 * @param {Object} user - User object with tier property
 * @returns {boolean} - True if user can send promotion kit
 */
export const canSendPromotionKit = (user) => {
  if (!user) return false;
  return getEffectiveTier(user) === TIERS.PRO;
};

/**
 * Get user tier display name
 * @param {Object} user - User object with tier property
 * @returns {string} - Display name for tier
 */
export const getTierDisplayName = (user) => {
  const effectiveTier = getEffectiveTier(user);
  if (!user || effectiveTier === TIERS.FREE) {
    return 'Free';
  }
  return 'Pro';
};

/**
 * Check if feature is available for user tier
 * @param {Object} user - User object with tier property
 * @param {string} feature - Feature name to check
 * @returns {boolean} - True if feature is available
 */
export const isFeatureAvailable = (user, feature) => {
  if (!user) return false;
  
  const tier = getEffectiveTier(user);
  const limits = TIER_LIMITS[tier];
  
  switch (feature) {
    case 'audiogram':
      return limits.audiogramEnabled;
    case 'promotionKit':
      return limits.promotionKitEnabled;
    case 'unlimitedQuotecards':
      return limits.unlimitedQuotecards;
    case 'quotecardDownload':
      return canDownloadQuotecard(user);
    default:
      return false;
  }
};
